import { useValidatedForm, validateEmail } from '@words-happen/screens';
import {
  SignInPrompt,
  WordsHappenButton,
  WordsHappenFormAlert,
  WordsHappenTextField,
} from '@words-happen/ui-components';
import type React from 'react';
import { useId, useRef } from 'react';
import { MessageScreen, messageScreenFormClassName, messageScreenStatusClassName } from '../../components';

export interface ForgotPasswordScreenProps {
  /** Prefills the email, e.g. the address typed on /sign-in. */
  defaultEmail?: string;
  /** A request is in flight: the submit button shows its spinner and Enter does nothing. */
  sending?: boolean;
  /** Whole seconds until another request is allowed, after being rate limited; 0 when it is. */
  cooldown?: number;
  /** Why the request failed (rate limited, network). Shown in a WordsHappenFormAlert above the submit button. */
  error?: string;
  /** Called with the trimmed address once it is well formed. */
  onSubmit: (email: string) => void;
  /** "Email me a sign-in link": its progress and cooldown (useResend). Errors share `error`. */
  signInLink?: { sending: boolean; cooldown: number };
  /** Called with the trimmed address once it is well formed. Without it there is no "Email me a sign-in link". */
  onSendSignInLink?: (email: string) => void;
  /** The back chevron and "Back to sign in", with the trimmed address typed so far. */
  onBack: (email: string) => void;
}

const VALIDATORS = { email: validateEmail };

/** "Reset your password": asks for the address to send a reset link, or a sign-in link, to. */
export const ForgotPasswordScreen: React.FC<ForgotPasswordScreenProps> = ({
  defaultEmail = '',
  sending = false,
  cooldown = 0,
  error,
  onSubmit,
  signInLink = { sending: false, cooldown: 0 },
  onSendSignInLink,
  onBack,
}) => {
  const { values, errors, change, blur, validateAll } = useValidatedForm({ email: defaultEmail }, VALIDATORS);
  const emailRef = useRef<HTMLInputElement>(null);
  const cooldownId = useId();
  const linkCooldownId = useId();
  const back = () => onBack(values.email.trim());

  /** The trimmed address once it is well formed; otherwise shows why and focuses the field. */
  const validEmail = (): string | undefined => {
    if (validateAll()) {
      emailRef.current?.focus();
      return undefined;
    }
    return values.email.trim();
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (sending || cooldown > 0) {
      return;
    }
    const email = validEmail();
    if (email) {
      onSubmit(email);
    }
  };

  const sendSignInLink = () => {
    if (signInLink.sending || signInLink.cooldown > 0) {
      return;
    }
    const email = validEmail();
    if (email) {
      onSendSignInLink?.(email);
    }
  };

  return (
    <MessageScreen
      title="Reset your password"
      mood="focused"
      onBack={back}
      action={
        <>
          <form
            className={messageScreenFormClassName}
            aria-label="Send a password reset link"
            noValidate
            onSubmit={handleSubmit}
          >
            <WordsHappenTextField
              ref={emailRef}
              label="Email"
              name="email"
              type="email"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              value={values.email}
              error={errors.email}
              onChange={change('email')}
              onBlur={blur('email')}
            />

            {error ? <WordsHappenFormAlert>{error}</WordsHappenFormAlert> : null}

            <WordsHappenButton
              type="submit"
              variant="coral"
              size="lg"
              loading={sending}
              disabled={cooldown > 0}
              aria-describedby={cooldown > 0 ? cooldownId : undefined}
            >
              Send reset link
            </WordsHappenButton>

            {onSendSignInLink ? (
              <WordsHappenButton
                size="lg"
                loading={signInLink.sending}
                disabled={signInLink.cooldown > 0}
                aria-describedby={signInLink.cooldown > 0 ? linkCooldownId : undefined}
                onClick={sendSignInLink}
              >
                Email me a sign-in link
              </WordsHappenButton>
            ) : null}
          </form>
          {cooldown > 0 ? (
            <p id={cooldownId} className={messageScreenStatusClassName}>
              You can try again in {cooldown}s
            </p>
          ) : null}
          {signInLink.cooldown > 0 ? (
            <p id={linkCooldownId} className={messageScreenStatusClassName}>
              You can ask for a sign-in link again in {signInLink.cooldown}s
            </p>
          ) : null}
          <SignInPrompt prefix={null} action="Back to sign in" onAction={back} />
        </>
      }
    >
      <p>
        Enter the email address for your account and we'll send you a link to choose a new password
        {onSendSignInLink ? ', or one to sign in straight away' : ''}.
      </p>
    </MessageScreen>
  );
};
